import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../../store';
import PaymentService from '../../../api/services/PaymentService';
import { PaymentHistoryResponse, PaymentMethodsResponse } from '../types';

const ACTIONS_PREFIX = 'payment';

interface PaymentState {
  isLoading: boolean;
  isPaymentsHistoryLoading: boolean;
  isSetPaymentMethodDefaultLoading: boolean;
  isRemoveCardLoading: boolean;
  paymentMethods: PaymentMethodsResponse[];
  paymentsHistory: PaymentHistoryResponse[];
  paymentsHistoryHasMore: boolean;
}

const initialState: PaymentState = {
  isLoading: false,
  isPaymentsHistoryLoading: false,
  isSetPaymentMethodDefaultLoading: false,
  isRemoveCardLoading: false,
  paymentMethods: [],
  paymentsHistory: [],
  paymentsHistoryHasMore: true,
};

export const getPaymentMethods = createAsyncThunk(
  `${ACTIONS_PREFIX}/getPaymentMethods`,
  async (patientID: string) => {
    return await PaymentService.getPaymentMethods(patientID);
  },
);

export const getPaymentsHistory = createAsyncThunk(
  `${ACTIONS_PREFIX}/getPaymentsHistory`,
  async (params: { patientID: string; limit: number; offset: number }) => {
    const { patientID, limit, offset } = params;
    return await PaymentService.getPaymentsHistory(patientID, limit, offset);
  },
);

export const setPaymentMethodDefault = createAsyncThunk(
  `${ACTIONS_PREFIX}/setPaymentMethodDefault`,
  async (params: { patientID: string; paymentMethodId: string }) => {
    const { patientID, paymentMethodId } = params;
    return await PaymentService.setPaymentMethodDefault(patientID, paymentMethodId);
  },
);

export const getClientSecretThunk = createAsyncThunk(
  `${ACTIONS_PREFIX}/getClientSecretThunk`,
  PaymentService.getClientSecret,
);

export const removePaymentMethod = createAsyncThunk(
  `${ACTIONS_PREFIX}/removePaymentMethod`,
  async (params: { patientID: string; paymentMethodId: string }) => {
    const { patientID, paymentMethodId } = params;
    return await PaymentService.removePaymentMethod(patientID, paymentMethodId);
  },
);

const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {
    clearPaymentData: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPaymentMethods.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getPaymentMethods.fulfilled, (state, action) => {
        state.isLoading = false;
        state.paymentMethods = action.payload.resource ?? [];
      })
      .addCase(getPaymentMethods.rejected, (state) => {
        state.isLoading = false;
      });

    builder
      .addCase(getPaymentsHistory.pending, (state) => {
        state.isPaymentsHistoryLoading = true;
      })
      .addCase(getPaymentsHistory.fulfilled, (state, action) => {
        state.isPaymentsHistoryLoading = false;
        if (Array.isArray(action.payload.resource)) {
          state.paymentsHistory = [...state.paymentsHistory, ...action.payload.resource];
          state.paymentsHistoryHasMore = action.payload.resource.length === 10;
        }
      })
      .addCase(getPaymentsHistory.rejected, (state) => {
        state.isPaymentsHistoryLoading = false;
        state.paymentsHistoryHasMore = false;
      });

    builder
      .addCase(setPaymentMethodDefault.pending, (state) => {
        state.isSetPaymentMethodDefaultLoading = true;
      })
      .addCase(setPaymentMethodDefault.fulfilled, (state) => {
        state.isSetPaymentMethodDefaultLoading = false;
      })
      .addCase(setPaymentMethodDefault.rejected, (state) => {
        state.isSetPaymentMethodDefaultLoading = false;
      });

    builder
      .addCase(getClientSecretThunk.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getClientSecretThunk.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(getClientSecretThunk.rejected, (state) => {
        state.isLoading = false;
      });

    builder
      .addCase(removePaymentMethod.pending, (state) => {
        state.isRemoveCardLoading = true;
      })
      .addCase(removePaymentMethod.fulfilled, (state) => {
        state.isRemoveCardLoading = false;
      })
      .addCase(removePaymentMethod.rejected, (state) => {
        state.isRemoveCardLoading = false;
      });
  },
});

export const { clearPaymentData } = paymentSlice.actions;

export const selectPayment = (state: RootState) => state.payments;
export default paymentSlice.reducer;
